/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/no-children-prop */
import normalizePath from '@/utils/normalizePath'
import clsx from 'clsx'
import Link from 'next/link'
import React, { CSSProperties, type FC, type ReactElement } from 'react'
import {
  IS_BOLD,
  IS_CODE,
  IS_ITALIC,
  IS_STRIKETHROUGH,
  IS_SUBSCRIPT,
  IS_SUPERSCRIPT,
  IS_UNDERLINE,
} from './RichTextNodeFormat'
import MediaComponent from '../media/media'
import { IndividualSnippetComponent } from '../snippets/individual-snippet'
import { BasicSnippetComponent } from '../snippets/basic-snippet'
import SmartLinksComponent from '../smart-links/smartLinks'
import { LegacyProfileSnippetComponent } from '../snippets/legacy-profile-snippet'
import RenderRichTexComponent from '../rich-text-render/richTextRender'
import EmbeddedVideo from '../embedded-video/embeddedVideo'
import { EmbeddedVideo as EmbeddedVideoBlock, MediaBlock as MediaBlockType } from '@/payload-types'
import Image from 'next/image'

type SerializedLexicalNode = {
  children?: SerializedLexicalNode[]
  direction: string
  format: number
  indent?: string | number
  type: string
  version: number
  style?: string
  mode?: string
  text?: string
  [other: string]: any
}

type TextComponentProps = {
  children: ReactElement | string
  format: number
  textOnly: boolean
}

const getLinkForDocument = (doc: any, locale?: string): string => {
  let path = doc?.path
  if (!path || path.startsWith('/home') || path === '/' || path === '') path = '/'
  if (!locale) return path
  return normalizePath(`/${locale}${path}`)
}

const TextComponent: FC<TextComponentProps> = ({ children, format, textOnly }) => {
  if (textOnly) return <span>{children}</span>
  const formatFunctions: { [key: number]: (child: ReactElement | string) => ReactElement } = {
    [IS_BOLD]: (child) => <strong>{child}</strong>,
    [IS_ITALIC]: (child) => <em>{child}</em>,
    [IS_STRIKETHROUGH]: (child) => <del>{child}</del>,
    [IS_UNDERLINE]: (child) => <u>{child}</u>,
    [IS_CODE]: (child) => <code>{child}</code>,
    [IS_SUBSCRIPT]: (child) => <sub>{child}</sub>,
    [IS_SUPERSCRIPT]: (child) => <sup>{child}</sup>,
  }

  const formattedText = Object.entries(formatFunctions).reduce(
    (formattedText, [key, formatter]) => {
      return format & Number(key) ? formatter(formattedText) : formattedText
    },
    children,
  )

  if (typeof formattedText === 'string') {
    console.log(formattedText)
  }

  return <span>{formattedText}</span>
}

const SerializedLink: React.FC<{
  node: SerializedLexicalNode
  locale: string
  children: JSX.Element | null
}> = ({ node, locale, children }) => {
  const { doc, url, linkType } = node.fields as any
  const document = doc?.value
  const href = linkType === 'custom' ? url : getLinkForDocument(document, locale)

  return (
    <Link href={href} className="text-blue-700 hover:underline" target="_blank">
      {children}
    </Link>
  )
}

const getNodeClassNames = (node: SerializedLexicalNode) => {
  const attributes: Record<string, any> = {}
  if (!node) return attributes

  let classNames = ''
  if (String(node?.format).toString()?.includes('left') && node.direction !== 'ltr')
    classNames += 'text-left '
  if (String(node?.format).toString()?.includes('center')) classNames += 'text-center '
  if (String(node?.format).toString()?.includes('right') && node.direction !== 'rtl')
    classNames += 'text-right '

  if (classNames.length > 0) attributes.className = classNames.trim()

  const indent = parseInt(`${node?.indent || 0}`)
  if (!isNaN(indent) && indent !== 0) {
    attributes.style = { '--indent': `${indent * 10}px` } as CSSProperties
    attributes.className = `${attributes.className ?? ''} ml-[--indent] max-w-full w-max`.trim()
  }

  return attributes
}

const LexicalContent: React.FC<{
  childrenNodes: SerializedLexicalNode[]
  locale: string
  className?: string
  lazyLoadImages: boolean
  textOnly: boolean
  inline?: boolean
}> = ({ childrenNodes, locale, lazyLoadImages = false, textOnly = false, inline = false }) => {
  if (!Array.isArray(childrenNodes)) return null
  const regex = /\s/g
  const renderedChildren = childrenNodes.map((node, ix) => {
    if (!node) return null
    const attributes = getNodeClassNames(node || '')
    if (node.type === 'text') {
      const text = node.text?.replace(regex, ' ')
      return (
        <TextComponent key={ix} format={node.format} textOnly={textOnly}>
          <span>
            {Object.keys(attributes).length > 0 && <span {...attributes}>{text || ''}</span>}
            {(Object.keys(attributes).length === 0 && text) || ''}
          </span>
        </TextComponent>
      )
    }

    const serializedChildren = node.children ? (
      <LexicalContent
        key={ix}
        childrenNodes={node.children}
        locale={locale}
        lazyLoadImages={lazyLoadImages}
        textOnly={textOnly}
      />
    ) : null

    switch (node.type) {
      case 'block':
        if (textOnly) return <></>
        switch (node?.fields.blockType) {
          case 'mediaBlock':
            return (
              <div className="my-[20px]">
                <MediaComponent mediaData={node?.fields as MediaBlockType} />
              </div>
            )
          case 'embeddedvideo':
            return (
              <div className="my-[20px]">
                <EmbeddedVideo embeddedVideoData={node?.fields as EmbeddedVideoBlock} />
              </div>
            )
        }
      case 'inlineImage':
        return (
          <Image
            src={node.src}
            alt={node.altText}
            width={20}
            height={20}
            className="inline relative m-[2px]"
          />
        )
      case 'inlineBlock':
        if (textOnly) return <>{node?.fields.textToDisplay?.replace(regex, ' ')}</>
        switch (node?.fields.blockType) {
          case 'snippet':
            if (node?.fields?.inlineSnippet === true) {
              return <RenderRichTexComponent richTextField={node?.fields?.snippet?.text} />
            } else {
              return (
                <BasicSnippetComponent
                  text={node?.fields.textToDisplay}
                  value={node?.fields?.snippet?.text}
                  name={node?.fields.snippet?.title}
                  id={node?.fields.snippet?.id}
                />
              )
            }

          case 'smartlink':
            return <SmartLinksComponent text={node?.fields.textToDisplay} smartLink={node.fields} />
          case 'legacyprofilelink':
            return <LegacyProfileSnippetComponent profileLink={node.fields} />
          case 'profilelink':
            return <IndividualSnippetComponent profileLink={node.fields} />
        }
      case 'linebreak':
        if (textOnly) return <></>
        return <br key={ix} />
      case 'snippet':
        const text = node.children?.map((item) => item?.text?.replace(regex, ' '))
        if (textOnly) {
          return (
            <TextComponent key={ix} format={node.format} textOnly={textOnly}>
              <span>
                {Object.keys(attributes).length > 0 && <span {...attributes}>{text || ''}</span>}
                {(Object.keys(attributes).length === 0 && text) || ''}
              </span>
            </TextComponent>
          )
        } else {
          return (
            <BasicSnippetComponent
              text={text}
              value={node?.fields?.doc?.text}
              name={node?.fields?.doc?.title}
              id={node?.fields?.doc?.id}
            />
          )
        }
      case 'link':
        if (textOnly) {
          const text = node.children?.map((item) => item.text?.replace(regex, ' '))
          return (
            <TextComponent key={ix} format={node.format} textOnly={textOnly}>
              <span>
                {Object.keys(attributes).length > 0 && <span {...attributes}>{text || ''}</span>}
                {(Object.keys(attributes).length === 0 && text) || ''}
              </span>
            </TextComponent>
          )
        }
        return <SerializedLink key={ix} node={node} locale={locale} children={serializedChildren} />
      case 'list':
        if (textOnly) return <></>
        const ListTag = node.listType === 'bullet' ? 'ul' : 'ol'
        attributes.className = clsx(
          attributes.className,
          'mb-4 pl-8 max-w-full w-max',
          ListTag === 'ol' ? 'list-decimal' : 'list-disc',
        )
        return (
          <ListTag key={ix} {...attributes}>
            {serializedChildren}
          </ListTag>
        )
      case 'listitem':
        if (textOnly) return <></>
        if (serializedChildren?.props && serializedChildren.props.childrenNodes) {
          const hasText = serializedChildren.props.childrenNodes.some(
            (node: any) => (node.text && node.text.trim() !== '') || node.type == 'link',
          )
          if (hasText) {
            return (
              <li key={ix} className="max-w-full w-max" {...attributes}>
                {serializedChildren}
              </li>
            )
          } else {
            return <>{serializedChildren}</>
          }
        }
      case 'heading':
        if (textOnly) return <></>
        const HeadingTag = node.tag as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
        return (
          <HeadingTag key={ix} {...attributes}>
            {serializedChildren}
          </HeadingTag>
        )
      case 'quote':
        if (textOnly) return <></>
        return (
          <blockquote key={ix} {...attributes}>
            {serializedChildren}
          </blockquote>
        )
      case 'mark':
        const mark = node.children?.map((item) => item.text?.replace(regex, ' '))
        return (
          <TextComponent key={ix} format={node.format} textOnly={textOnly}>
            <span>
              {Object.keys(attributes).length > 0 && <span {...attributes}>{mark || ''}</span>}
              {(Object.keys(attributes).length === 0 && mark) || ''}
            </span>
          </TextComponent>
        )
      case 'paragraph':
        if (
          node.children?.at(0)?.type === 'inlineBlock' &&
          node.children?.at(0)?.fields.blockType === 'snippet'
        ) {
          return (
            <div key={ix} {...attributes}>
              {serializedChildren}
            </div>
          )
        }
      default:
        if (
          Array.isArray(serializedChildren?.props?.childrenNodes) &&
          serializedChildren?.props?.childrenNodes.length === 0 &&
          !textOnly
        )
          return <br key={ix} />
        return (
          <p key={ix} {...attributes} className={`${attributes.className ?? ''} ${inline ? 'inline' : ''}`}>
            {serializedChildren}
          </p>
        )
    }
  })

  return <>{renderedChildren.filter((node) => node !== null)}</>
}

export default LexicalContent
